import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiPlus, FiEdit2, FiSave, FiFileText, FiTrash2, FiX } from 'react-icons/fi';
import { procesosService } from '../../services';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import { labelDepartamento, labelCategoria } from '../../utils/productLabels';
import { toLocalDateString, parseLocalDate } from '../../utils';
import './ProcesosList.scss';

// Mapeo de la última parte de la URL al tipo de proceso.
// La URL es la fuente de verdad (sobrevive a refrescar / links directos).
const PATH_TO_PROCESS_TYPE = {
  encapsulado: 'ENCAPSULATION',
  tes: 'TEA_PACKING',
  empacado: 'PACKAGING',
  limpieza: 'CLEANING',
  recibir: 'RECEIVING',
  retornos: 'RETURNS',
  mantenimiento: 'MAINTENANCE'
};

/**
 * Vista de lista de procesos con edición inline
 * Basado en el diseño del PDF del cliente (páginas 2-7)
 */
const ProcesosList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Derivar el tipo desde la URL (no del state de navegación, que se pierde al
  // refrescar). El state y ENCAPSULATION quedan solo como respaldo.
  const lastSegment = location.pathname.split('/').filter(Boolean).pop();
  const processType =
    PATH_TO_PROCESS_TYPE[lastSegment] ||
    location.state?.processType ||
    'ENCAPSULATION';

  const [procesos, setProcesos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [newRow, setNewRow] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => {
    loadProcesos();
  }, [processType]);

  const loadProcesos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await procesosService.getByType(processType, { limit: 100 });
      if (result.success) {
        setProcesos(result.data || []);
      } else {
        setError(result.message || 'No se pudieron cargar los registros.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getPageTitle = () => {
    const titles = {
      'ENCAPSULATION': 'REGISTROS\nDE ENCAPSULADO',
      'TEA_PACKING': 'REGISTROS\nDE TÉS',
      'PACKAGING': 'REGISTROS\nDE EMPACADO',
      'CLEANING': 'REGISTROS\nDE LIMPIEZA',
      'RECEIVING': 'REGISTROS\nPARA RECIBIR\nPRODUCTOS',
      'RETURNS': 'REGISTROS DE\nRETORNO DE\nPRODUCTOS',
      'MAINTENANCE': 'REGISTRO\nDE MANTENIMIENTO'
    };
    return titles[processType] || 'REGISTROS';
  };

  const handleAddNew = () => {
    // Abrir el formulario en modal (campos grandes) en modo "crear"
    setEditingRecord(null);
    setShowFormModal(true);
  };

  const handleEdit = (row) => {
    // Abrir el mismo modal pre-cargado en modo "editar"
    setEditingRecord(row);
    setShowFormModal(true);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setEditingRecord(null);
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    // handleSave decide crear/actualizar según formData.id ('new' = crear).
    // Si tiene éxito, cierra el modal y recarga la lista.
    await handleSave(formData);
    setIsSubmitting(false);
  };

  const handleSave = async (proceso) => {
    try {
      let result;
      if (proceso.id === 'new') {
        // Separar campos según la estructura correcta de la API
        const productionProcess = {
          process_type: processType,
          product_name: proceso.product_name || '',
          process_date: proceso.process_date || toLocalDateString(),
          operator_name: proceso.operator_name || '',
          notes: proceso.notes || ''
        };

        // Si el producto se eligió del buscador, guardar también su ID:
        // los reportes (p. ej. Retornos por producto) cruzan por product_id
        // y con solo el nombre escrito a mano se perdía la trazabilidad.
        if (proceso.product_id) {
          productionProcess.product_id = proceso.product_id;
        }

        // Agregar batch_number si existe
        if (proceso.batch_number?.trim()) {
          productionProcess.batch_number = proceso.batch_number.trim();
        }

        // Construir process_details según el tipo de proceso
        const processDetails = {};

        switch (processType) {
          case 'TEA_PACKING':
            processDetails.packed_units = parseInt(proceso.packed_units) || 0;
            processDetails.packer_name = proceso.packer_name || '';
            if (proceso.company_name?.trim()) {
              processDetails.company_name = proceso.company_name.trim();
            } else {
              processDetails.company_name = null;
            }
            break;

          case 'PACKAGING':
            processDetails.packed_units = parseInt(proceso.packed_units) || 0;
            processDetails.units_per_container = 1; // Valor fijo en 1
            processDetails.total_containers = 1; // Valor fijo en 1
            processDetails.packer_name = proceso.packer_name || '';
            if (proceso.company_name?.trim()) {
              processDetails.company_name = proceso.company_name.trim();
            } else {
              processDetails.company_name = null;
            }
            break;

          case 'ENCAPSULATION':
            processDetails.net_weight_per_capsule = parseFloat(proceso.net_weight_per_capsule) || 0;
            processDetails.actual_weight_per_capsule = parseFloat(proceso.actual_weight_per_capsule) || 0;
            processDetails.bottles_produced = parseInt(proceso.bottles_produced) || 0;
            break;

          case 'CLEANING':
            processDetails.deep_cleaning = proceso.deep_cleaning || false;
            processDetails.weekly_cleaning = proceso.weekly_cleaning || false;
            processDetails.production_area = proceso.production_area || false;
            processDetails.warehouse_area = proceso.warehouse_area || false;
            processDetails.outside_area = proceso.outside_area || false;
            processDetails.bathroom_area = proceso.bathroom_area || proceso.restroom || false;
            processDetails.cleaner_name = proceso.cleaner_name || proceso.operator_name || '';
            break;

          case 'RECEIVING':
            processDetails.received_boxes = parseInt(proceso.received_boxes) || 0;
            processDetails.weight_per_box = parseFloat(proceso.weight_per_box) || 0;
            processDetails.total_received = parseFloat(proceso.total_received) || 0;
            processDetails.supplier_name = proceso.supplier_name || proceso.company_name || '';
            processDetails.received_by_name = proceso.received_by_name || proceso.receiver_name || '';
            break;

          case 'RETURNS':
            processDetails.returned_quantity = parseInt(proceso.returned_quantity || proceso.quantity_returned) || 0;
            processDetails.client_name = proceso.client_name || '';
            processDetails.return_status = proceso.return_status || 'GOOD';
            // Si el cliente se eligió del buscador, guardar su ID (trazabilidad)
            if (proceso.client_id) {
              processDetails.client_id = proceso.client_id;
            }
            // Motivo del retorno: sin él, los reportes salían "No especificado"
            if (proceso.return_reason?.trim()) {
              processDetails.return_reason = proceso.return_reason.trim();
            }
            break;

          case 'MAINTENANCE':
            processDetails.equipment_name = proceso.equipment_name || '';
            processDetails.maintenance_type = 'PREVENTIVE'; // Valor fijo
            processDetails.technician_name = proceso.technician_name || '';
            processDetails.parts_replaced = 'N/A'; // Valor fijo
            processDetails.total_cost = 0; // Valor fijo
            processDetails.is_operational = true; // Valor fijo
            processDetails.description = proceso.description || proceso.notes || '';
            break;

          default:
            // Para otros tipos, incluir todo excepto campos comunes
            Object.keys(proceso).forEach(key => {
              if (!['id', 'product_name', 'process_date', 'operator_name', 'batch_number'].includes(key)) {
                processDetails[key] = proceso[key];
              }
            });
        }

        result = await procesosService.createProceso({
          process_type: processType,
          production_process: productionProcess,
          process_details: processDetails
        });
        setNewRow(null);
      } else {
        // Usar el production_process_id para actualizar
        const productionProcessId = proceso.production_process_id || proceso.production_process?.id;

        if (!productionProcessId) {
          console.error('No se encontró production_process_id para actualizar', proceso);
          return;
        }

        // Para los endpoints PUT específicos, enviar solo los campos del proceso específico
        let updateData = {};

        switch (processType) {
          case 'TEA_PACKING':
            updateData = {
              packed_units: parseInt(proceso.packed_units) || 0,
              packer_name: proceso.packer_name || ''
            };
            if (proceso.company_name?.trim()) {
              updateData.company_name = proceso.company_name.trim();
            }
            result = await procesosService.updateTeaPacking(productionProcessId, updateData);
            break;

          case 'PACKAGING':
            updateData = {
              packed_units: parseInt(proceso.packed_units) || 0,
              units_per_container: 1,
              total_containers: 1,
              packer_name: proceso.packer_name || ''
            };
            if (proceso.company_name?.trim()) {
              updateData.company_name = proceso.company_name.trim();
            }
            result = await procesosService.updatePackaging(productionProcessId, updateData);
            break;

          case 'ENCAPSULATION':
            updateData = {
              net_weight_per_capsule: parseFloat(proceso.net_weight_per_capsule) || 0,
              actual_weight_per_capsule: parseFloat(proceso.actual_weight_per_capsule) || 0,
              bottles_produced: parseInt(proceso.bottles_produced) || 0
            };
            result = await procesosService.updateEncapsulation(productionProcessId, updateData);
            break;

          case 'CLEANING':
            updateData = {
              deep_cleaning: proceso.deep_cleaning || false,
              weekly_cleaning: proceso.weekly_cleaning || false,
              production_area: proceso.production_area || false,
              warehouse_area: proceso.warehouse_area || false,
              outside_area: proceso.outside_area || false,
              bathroom_area: proceso.bathroom_area || false,
              cleaner_name: proceso.cleaner_name || ''
            };
            result = await procesosService.updateCleaning(productionProcessId, updateData);
            break;

          case 'RECEIVING':
            updateData = {
              received_boxes: parseInt(proceso.received_boxes) || 0,
              weight_per_box: parseFloat(proceso.weight_per_box) || 0,
              total_received: parseFloat(proceso.total_received) || 0,
              supplier_name: proceso.supplier_name || '',
              received_by_name: proceso.received_by_name || ''
            };
            result = await procesosService.updateReceiving(productionProcessId, updateData);
            break;

          case 'RETURNS':
            updateData = {
              return_status: proceso.return_status || 'GOOD'
            };
            // El backend valida cantidad > 0 y nombre no vacío: enviar solo
            // valores válidos para no tumbar toda la actualización.
            if (parseInt(proceso.returned_quantity) > 0) {
              updateData.returned_quantity = parseInt(proceso.returned_quantity);
            }
            if (proceso.client_name?.trim()) {
              updateData.client_name = proceso.client_name.trim();
            }
            if (proceso.client_id) {
              updateData.client_id = proceso.client_id;
            }
            if (proceso.return_reason?.trim()) {
              updateData.return_reason = proceso.return_reason.trim();
            }
            result = await procesosService.updateReturns(productionProcessId, updateData);
            break;

          case 'MAINTENANCE':
            updateData = {
              equipment_name: proceso.equipment_name || '',
              maintenance_type: 'PREVENTIVE',
              technician_name: proceso.technician_name || '',
              parts_replaced: 'N/A',
              total_cost: 0,
              is_operational: true,
              description: proceso.description || ''
            };
            result = await procesosService.updateMaintenance(productionProcessId, updateData);
            break;

          default:
            // Para tipos desconocidos, usar el método genérico
            result = await procesosService.updateProceso(productionProcessId, proceso);
        }
      }

      if (result && result.success) {
        // Los campos comunes (producto, fecha, lote, notas) viven en
        // production_process; en edición se persisten con el PUT genérico.
        // (En creación ya van incluidos en el payload de createProceso)
        if (proceso.id !== 'new') {
          const ppId = proceso.production_process_id || proceso.production_process?.id;
          if (ppId) {
            const commonUpdate = { notes: proceso.notes ?? '' };
            // product_name/operator_name exigen min_length=1 en el backend:
            // solo enviarlos si no están vacíos.
            if (proceso.product_name && proceso.product_name.trim()) {
              commonUpdate.product_name = proceso.product_name.trim();
            }
            if (proceso.operator_name && proceso.operator_name.trim()) {
              commonUpdate.operator_name = proceso.operator_name.trim();
            }
            if (proceso.process_date) {
              commonUpdate.process_date = proceso.process_date;
            }
            if (proceso.batch_number !== undefined) {
              commonUpdate.batch_number = proceso.batch_number || null;
            }
            try {
              await procesosService.updateProceso(ppId, commonUpdate);
            } catch (commonErr) {
              console.error('No se pudieron guardar los campos comunes:', commonErr);
            }
          }
        }
        loadProcesos();
        setEditingId(null);
        setShowFormModal(false);
        setEditingRecord(null);
      } else {
        alert((result && result.message) || 'No se pudo guardar el registro.');
      }
    } catch (err) {
      console.error('Error saving:', err);
      alert('Error al guardar: ' + (err.message || 'error desconocido'));
    }
  };

  const handleDelete = async (proceso) => {
    // Si es una fila nueva sin guardar, solo se descarta
    if (proceso.id === 'new') {
      setNewRow(null);
      setEditingId(null);
      return;
    }

    const ppId = proceso.production_process_id || proceso.production_process?.id || proceso.id;
    if (!ppId) return;

    if (!window.confirm('¿Eliminar este registro? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const result = await procesosService.deleteProceso(ppId);
      if (result && result.success) {
        loadProcesos();
      } else {
        alert((result && result.message) || 'No se pudo eliminar el registro.');
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error al eliminar: ' + (err.message || 'error desconocido'));
    }
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);
    try {
      const columns = getExportColumns(processType);
      const inRange = (row) => {
        const d = (row.production_process?.process_date || row.process_date || '').slice(0, 10);
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
        return true;
      };
      const rows = procesos.filter(inRange);
      const title = getPageTitle().replace(/\n/g, ' ');
      const stamp = toLocalDateString();
      const fileBase = `procesos_${processType.toLowerCase()}_${stamp}`;

      if (format === 'csv') {
        const csv = buildCsv(columns, rows);
        // BOM para que Excel respete los acentos
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        triggerDownload(blob, `${fileBase}.csv`);
      } else {
        printReport(title, columns, rows);
      }
      setShowDownloadModal(false);
    } catch (err) {
      console.error('Error al exportar:', err);
      alert('Error al generar el reporte.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando registros..." />;
  }

  return (
    <div className="procesos-list-page">
      {/* Header */}
      <div className="procesos-list-header">
        <div>
          <h1 className="procesos-list-title">
            {getPageTitle().split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < getPageTitle().split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
        </div>
        <div className="procesos-list-actions">
          <button
            className="btn btn--outline"
            onClick={() => navigate('/system/procesos')}
          >
            REGRESAR AL MENÚ
          </button>
          <button
            className="btn btn--primary"
            onClick={() => setShowDownloadModal(true)}
          >
            <FiFileText size={18} />
            DESCARGAR LISTA
          </button>
        </div>
      </div>

      {error && (
        <div className="procesos-error-banner">{error}</div>
      )}

      {/* Tabla según tipo de proceso */}
      <div className="procesos-table-container">
        {renderTableByType(processType, procesos, newRow, editingId, handleEdit, handleSave, handleAddNew, handleDelete)}
      </div>

      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title={`Descargar ${getPageTitle().replace(/\n/g, ' ')}`}
        moduleName="procesos"
        isLoading={isDownloading}
      />

      <ProcesoFormModal
        isOpen={showFormModal}
        processType={processType}
        record={editingRecord}
        title={`${editingRecord ? 'Editar' : 'Nuevo'} registro — ${getPageTitle().replace(/\n/g, ' ')}`}
        onClose={handleCloseFormModal}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

// Función auxiliar para crear fila vacía según tipo
const createEmptyRow = (type) => {
  const baseRow = {
    id: 'new',
    product_name: '',
    process_date: toLocalDateString(),
    operator_name: '',
    notes: ''
  };

  switch (type) {
    case 'ENCAPSULATION':
      return {
        ...baseRow,
        net_weight_per_capsule: 0,
        actual_weight_per_capsule: 0,
        bottles_produced: 0
      };
    case 'TEA_PACKING':
    case 'PACKAGING':
      return {
        ...baseRow,
        packed_units: 0,
        batch_number: '',
        company_name: '',
        packer_name: ''
      };
    case 'CLEANING':
      return {
        ...baseRow,
        deep_cleaning: false,
        weekly_cleaning: false,
        production_area: false,
        warehouse_area: false,
        outside_area: false,
        bathroom_area: false,
        cleaner_name: ''
      };
    case 'RECEIVING':
      return {
        ...baseRow,
        received_boxes: 0,
        weight_per_box: 0,
        total_received: 0,
        supplier_name: '',
        received_by_name: ''
      };
    case 'RETURNS':
      return {
        ...baseRow,
        returned_quantity: 0,
        client_name: '',
        return_status: 'GOOD'
      };
    case 'MAINTENANCE':
      return {
        ...baseRow,
        equipment_name: '',
        technician_name: '',
        maintenance_type: 'PREVENTIVE',
        parts_replaced: 'N/A',
        total_cost: 0,
        is_operational: true,
        description: ''
      };
    default:
      return baseRow;
  }
};

// Renderizar tabla según tipo de proceso
const renderTableByType = (type, procesos, newRow, editingId, handleEdit, handleSave, handleAddNew, handleDelete) => {
  const allRows = newRow ? [newRow, ...procesos] : procesos;
  const common = {
    rows: allRows,
    editingId,
    onEdit: handleEdit,
    onSave: handleSave,
    onAddNew: handleAddNew,
    onDelete: handleDelete
  };

  switch (type) {
    case 'ENCAPSULATION':
      return <EncapsulationTable {...common} />;
    case 'TEA_PACKING':
      return <TeaPackingTable {...common} />;
    case 'PACKAGING':
      return <PackagingTable {...common} />;
    case 'CLEANING':
      return <CleaningTable {...common} />;
    case 'RECEIVING':
      return <ReceivingTable {...common} />;
    case 'RETURNS':
      return <ReturnsTable {...common} />;
    case 'MAINTENANCE':
      return <MaintenanceTable {...common} />;
    default:
      return <div>Tabla no implementada para tipo: {type}</div>;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente compartido de acciones por fila (Editar / Guardar / Eliminar)
// ─────────────────────────────────────────────────────────────────────────────
const RowActions = ({ isEditing, isNew, onSave, onEdit, onDelete }) => (
  isEditing ? (
    <button className="icon-btn icon-btn--save" onClick={onSave} title="Guardar" aria-label="Guardar">
      <FiSave size={18} />
    </button>
  ) : (
    <div className="row-actions">
      <button className="icon-btn icon-btn--edit" onClick={onEdit} title="Editar" aria-label="Editar">
        <FiEdit2 size={18} />
      </button>
      {!isNew && (
        <button className="icon-btn icon-btn--delete" onClick={onDelete} title="Eliminar" aria-label="Eliminar">
          <FiTrash2 size={18} />
        </button>
      )}
    </div>
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// Formulario en modal para crear un nuevo registro (campos grandes)
// ─────────────────────────────────────────────────────────────────────────────
const getFormFields = (type) => {
  // type 'product': input con buscador (datalist) sobre los productos reales;
  // al elegir uno se guarda también product_id para la trazabilidad.
  const product = { key: 'product_name', label: 'Nombre del producto', type: 'product' };
  const date = { key: 'process_date', label: 'Fecha', type: 'date' };
  const notes = { key: 'notes', label: 'Notas', type: 'textarea' };

  switch (type) {
    case 'ENCAPSULATION':
      return [
        product,
        { ...date, label: 'Fecha de producción' },
        { key: 'net_weight_per_capsule', label: 'Peso neto por cápsula', type: 'number' },
        { key: 'actual_weight_per_capsule', label: 'Peso por cápsula', type: 'number' },
        { key: 'bottles_produced', label: 'Cantidad producida (botellas)', type: 'number' },
        { key: 'operator_name', label: 'Nombre del operador', type: 'text' },
        notes
      ];
    case 'TEA_PACKING':
    case 'PACKAGING':
      return [
        product,
        { ...date, label: 'Fecha de empaquetado' },
        { key: 'packed_units', label: 'Unidades empaquetadas', type: 'number' },
        { key: 'batch_number', label: 'Número de lote', type: 'text' },
        { key: 'company_name', label: 'Nombre de compañía', type: 'client' },
        { key: 'packer_name', label: 'Nombre del empacador', type: 'text' },
        notes
      ];
    case 'CLEANING':
      return [
        date,
        { key: 'deep_cleaning', label: 'Limpieza profunda', type: 'checkbox' },
        { key: 'weekly_cleaning', label: 'Limpieza semanal', type: 'checkbox' },
        { key: 'production_area', label: 'Área de producción', type: 'checkbox' },
        { key: 'warehouse_area', label: 'Área de almacén', type: 'checkbox' },
        { key: 'outside_area', label: 'Área exterior', type: 'checkbox' },
        { key: 'bathroom_area', label: 'Baño', type: 'checkbox' },
        { key: 'cleaner_name', label: 'Nombre del responsable', type: 'text' },
        notes
      ];
    case 'RECEIVING':
      return [
        product,
        date,
        { key: 'received_boxes', label: 'Cajas recibidas', type: 'number' },
        { key: 'weight_per_box', label: 'Peso por caja', type: 'number' },
        { key: 'total_received', label: 'Total recibido', type: 'number' },
        { key: 'supplier_name', label: 'Compañía / Proveedor', type: 'supplier' },
        { key: 'received_by_name', label: 'Recibido por', type: 'text' },
        notes
      ];
    case 'RETURNS':
      return [
        product,
        date,
        // El backend exige cantidad > 0 y cliente con nombre: marcarlos
        // requeridos evita el 422 con alert genérico al guardar.
        { key: 'returned_quantity', label: 'Cantidad retornada', type: 'number', required: true, min: 1 },
        { key: 'client_name', label: 'Nombre del cliente', type: 'client', required: true },
        {
          key: 'return_status',
          label: 'Estado del producto',
          type: 'select',
          options: [{ value: 'GOOD', label: 'Bueno' }, { value: 'BAD', label: 'Malo' }]
        },
        // El reporte de retornos mostraba "Motivo: No especificado" porque
        // este campo no existía en el formulario de registro.
        { key: 'return_reason', label: 'Motivo del retorno', type: 'textarea' },
        notes
      ];
    case 'MAINTENANCE':
      return [
        { key: 'equipment_name', label: 'Nombre del equipo', type: 'text' },
        date,
        { key: 'technician_name', label: 'Nombre del técnico', type: 'text' },
        { key: 'description', label: 'Nota', type: 'textarea' }
      ];
    default:
      return [product, date, notes];
  }
};

// Campos comunes que la API devuelve anidados dentro de production_process
const NESTED_PROCESO_KEYS = ['product_name', 'process_date', 'operator_name', 'batch_number', 'notes'];

// Construye el estado inicial del formulario a partir de un registro existente,
// leyendo correctamente los campos anidados (production_process) y los planos.
const flattenProceso = (record, type) => {
  const out = createEmptyRow(type);
  getFormFields(type).forEach((f) => {
    let v = NESTED_PROCESO_KEYS.includes(f.key)
      ? (record.production_process?.[f.key] ?? record[f.key])
      : record[f.key];
    if (f.key === 'process_date' && v) v = String(v).slice(0, 10);
    if (v !== undefined && v !== null) out[f.key] = v;
  });
  // Conservar identificadores para la actualización
  out.id = record.id;
  out.production_process_id = record.production_process_id || record.production_process?.id;
  out.production_process = record.production_process;
  // IDs de trazabilidad (buscadores de producto y cliente)
  const productId = record.product_id ?? record.production_process?.product_id;
  if (productId) out.product_id = productId;
  if (record.client_id) out.client_id = record.client_id;
  return out;
};

const ProcesoFormModal = ({ isOpen, processType, record, title, onClose, onSubmit, isSubmitting }) => {
  const [form, setForm] = useState({});
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setForm(record ? flattenProceso(record, processType) : createEmptyRow(processType));
    }
  }, [isOpen, processType, record]);

  const fields = getFormFields(processType);
  const needsClientes = fields.some((f) => f.type === 'client');
  const needsProveedores = fields.some((f) => f.type === 'supplier');

  // Cargar el catálogo para el buscador de productos (datalist): antes el
  // nombre era texto libre, con errores de digitación y sin trazabilidad.
  useEffect(() => {
    if (!isOpen || productos.length > 0) return;
    (async () => {
      try {
        const { productosService } = await import('../../services');
        const result = await productosService.getProductos({ status: 'ACTIVE', limit: 1000 });
        if (result.success) setProductos(result.data || []);
      } catch (e) {
        console.error('Error cargando productos para el buscador:', e);
      }
    })();
  }, [isOpen, productos.length]);

  // Catálogos para los buscadores de compañía/cliente (empacado, tés,
  // retornos) y de proveedor (recepción): mismos nombres que el sistema,
  // para que la trazabilidad no dependa de cómo se digitó el nombre.
  useEffect(() => {
    if (!isOpen || !needsClientes || clientes.length > 0) return;
    (async () => {
      try {
        const { clientesService } = await import('../../services');
        // El endpoint /clients limita 'limit' a 500 (le=500): pedir más
        // devuelve 422 y la lista llegaba vacía (buscador sin sugerencias).
        const result = await clientesService.getClientes({ limit: 500 });
        if (result.success) setClientes(result.data || []);
      } catch (e) {
        console.error('Error cargando clientes para el buscador:', e);
      }
    })();
  }, [isOpen, needsClientes, clientes.length]);

  useEffect(() => {
    if (!isOpen || !needsProveedores || proveedores.length > 0) return;
    (async () => {
      try {
        const { proveedoresService } = await import('../../services');
        const result = await proveedoresService.getProveedores({ limit: 1000 });
        if (result.success) setProveedores(result.data || []);
      } catch (e) {
        console.error('Error cargando proveedores para el buscador:', e);
      }
    })();
  }, [isOpen, needsProveedores, proveedores.length]);

  if (!isOpen) return null;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const clienteNombre = (c) => c.company_name || c.full_name || '';

  // Al escribir/elegir el producto, resolver su ID si el nombre coincide
  // exactamente con uno del catálogo (los reportes cruzan por product_id).
  const handleProductNameChange = (value) => {
    const match = productos.find(
      (p) => (p.name || '').toLowerCase() === value.trim().toLowerCase()
    );
    setForm((prev) => ({
      ...prev,
      product_name: value,
      product_id: match ? match.id : null
    }));
  };

  // Igual que el producto: si el nombre coincide con un cliente del catálogo,
  // conservar su ID (los retornos guardan client_id para la trazabilidad).
  const handleClientNameChange = (key, value) => {
    const match = clientes.find(
      (c) => clienteNombre(c).toLowerCase() === value.trim().toLowerCase()
    );
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'client_name' ? { client_id: match ? match.id : null } : {})
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(form);
  };

  return (
    <div className="proceso-form-modal-overlay" onClick={onClose}>
      <div className="proceso-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pfm-header">
          <h2 className="pfm-title">{title}</h2>
          <button className="pfm-close" type="button" onClick={onClose} disabled={isSubmitting} aria-label="Cerrar">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="pfm-body">
            {fields.map((f) => (
              <div
                key={f.key}
                className={`pfm-field${f.type === 'checkbox' ? ' pfm-field--checkbox' : ''}${f.type === 'textarea' ? ' pfm-field--full' : ''}`}
              >
                {f.type === 'checkbox' ? (
                  <label className="pfm-check">
                    <input
                      type="checkbox"
                      checked={!!form[f.key]}
                      onChange={(e) => setField(f.key, e.target.checked)}
                    />
                    <span>{f.label}</span>
                  </label>
                ) : (
                  <>
                    <label className="pfm-label">{f.label}</label>
                    {f.type === 'textarea' ? (
                      <textarea
                        className="pfm-input"
                        rows="3"
                        value={form[f.key] ?? ''}
                        onChange={(e) => setField(f.key, e.target.value)}
                      />
                    ) : f.type === 'select' ? (
                      <select
                        className="pfm-input"
                        value={form[f.key] ?? ''}
                        onChange={(e) => setField(f.key, e.target.value)}
                      >
                        {f.options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : f.type === 'product' ? (
                      <>
                        <input
                          type="text"
                          className="pfm-input"
                          list="pfm-productos-list"
                          placeholder="Escribe para buscar el producto..."
                          value={form[f.key] ?? ''}
                          onChange={(e) => handleProductNameChange(e.target.value)}
                          required={!!f.required}
                        />
                        <datalist id="pfm-productos-list">
                          {productos.map((p) => (
                            // El texto de la opción muestra departamento y
                            // categoría para distinguir productos homónimos
                            // (producto terminado vs material de empaque).
                            <option key={p.id} value={p.name}>
                              {[labelDepartamento(p.product_type), labelCategoria(p.category)]
                                .filter(Boolean)
                                .join(' · ')}
                            </option>
                          ))}
                        </datalist>
                        {form.product_name && !form.product_id && (
                          <small style={{ color: '#b45309', fontSize: 11 }}>
                            Selecciona un producto de la lista para conservar la trazabilidad
                          </small>
                        )}
                      </>
                    ) : f.type === 'client' ? (
                      <>
                        <input
                          type="text"
                          className="pfm-input"
                          list="pfm-clientes-list"
                          placeholder="Escribe para buscar la compañía/cliente..."
                          value={form[f.key] ?? ''}
                          onChange={(e) => handleClientNameChange(f.key, e.target.value)}
                          required={!!f.required}
                        />
                        <datalist id="pfm-clientes-list">
                          {clientes.map((c) => (
                            <option key={c.id} value={clienteNombre(c)} />
                          ))}
                        </datalist>
                      </>
                    ) : f.type === 'supplier' ? (
                      <>
                        <input
                          type="text"
                          className="pfm-input"
                          list="pfm-proveedores-list"
                          placeholder="Escribe para buscar el proveedor..."
                          value={form[f.key] ?? ''}
                          onChange={(e) => setField(f.key, e.target.value)}
                          required={!!f.required}
                        />
                        <datalist id="pfm-proveedores-list">
                          {proveedores.map((p) => (
                            <option key={p.id} value={p.name} />
                          ))}
                        </datalist>
                      </>
                    ) : (
                      <input
                        type={f.type}
                        className="pfm-input"
                        value={form[f.key] ?? ''}
                        onChange={(e) => setField(f.key, e.target.value)}
                        required={!!f.required}
                        min={f.min}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="pfm-footer">
            <button type="button" className="btn btn--outline" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
              <FiSave size={18} />
              {isSubmitting ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Exportación de la lista (CSV / PDF) — generada en el frontend
// ─────────────────────────────────────────────────────────────────────────────
const getExportColumns = (type) => {
  const product = { label: 'Producto', value: (r) => r.production_process?.product_name ?? r.product_name ?? '' };
  const date = { label: 'Fecha', value: (r) => (r.production_process?.process_date ?? r.process_date ?? '').slice(0, 10) };
  const notes = { label: 'Notas', value: (r) => r.production_process?.notes ?? r.notes ?? '' };

  switch (type) {
    case 'ENCAPSULATION':
      return [
        product, date,
        { label: 'Peso neto/cápsula', value: (r) => r.net_weight_per_capsule ?? '' },
        { label: 'Peso/cápsula', value: (r) => r.actual_weight_per_capsule ?? '' },
        { label: 'Botellas producidas', value: (r) => r.bottles_produced ?? '' },
        { label: 'Operador', value: (r) => r.production_process?.operator_name ?? r.operator_name ?? '' },
        notes
      ];
    case 'TEA_PACKING':
    case 'PACKAGING':
      return [
        product, date,
        { label: 'Unidades empacadas', value: (r) => r.packed_units ?? '' },
        { label: 'Lote', value: (r) => r.production_process?.batch_number ?? r.batch_number ?? '' },
        { label: 'Compañía', value: (r) => r.company_name ?? '' },
        { label: 'Empacador', value: (r) => r.packer_name ?? '' },
        notes
      ];
    case 'CLEANING':
      return [
        date,
        { label: 'Limpieza profunda', value: (r) => (r.deep_cleaning ? 'Sí' : 'No') },
        { label: 'Limpieza semanal', value: (r) => (r.weekly_cleaning ? 'Sí' : 'No') },
        { label: 'Producción', value: (r) => (r.production_area ? 'Sí' : 'No') },
        { label: 'Almacén', value: (r) => (r.warehouse_area ? 'Sí' : 'No') },
        { label: 'Exterior', value: (r) => (r.outside_area ? 'Sí' : 'No') },
        { label: 'Baño', value: (r) => ((r.bathroom_area || r.restroom) ? 'Sí' : 'No') },
        { label: 'Nombre', value: (r) => r.cleaner_name ?? r.production_process?.operator_name ?? r.operator_name ?? '' },
        notes
      ];
    case 'RECEIVING':
      return [
        product, date,
        { label: 'Cajas recibidas', value: (r) => r.received_boxes ?? '' },
        { label: 'Peso/caja', value: (r) => r.weight_per_box ?? '' },
        { label: 'Total recibido', value: (r) => r.total_received ?? '' },
        { label: 'Compañía', value: (r) => r.supplier_name ?? r.company_name ?? '' },
        { label: 'Recibido por', value: (r) => r.received_by_name ?? r.receiver_name ?? '' },
        notes
      ];
    case 'RETURNS':
      return [
        product, date,
        { label: 'Cantidad retornada', value: (r) => r.returned_quantity ?? r.quantity_returned ?? '' },
        { label: 'Cliente', value: (r) => r.client_name ?? '' },
        { label: 'Estado', value: (r) => (r.return_status === 'GOOD' ? 'Bueno' : r.return_status === 'BAD' ? 'Malo' : (r.return_status ?? '')) },
        notes
      ];
    case 'MAINTENANCE':
      return [
        { label: 'Equipo', value: (r) => r.equipment_name ?? '' },
        date,
        { label: 'Técnico', value: (r) => r.technician_name ?? '' },
        { label: 'Nota', value: (r) => r.description ?? r.notes ?? '' }
      ];
    default:
      return [product, date, notes];
  }
};

const escapeCsv = (val) => {
  const s = val == null ? '' : String(val);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (columns, rows) => {
  const head = columns.map((c) => escapeCsv(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => escapeCsv(c.value(r))).join(','));
  return [head, ...body].join('\r\n');
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const printReport = (title, columns, rows) => {
  const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const thead = columns.map((c) => `<th>${esc(c.label)}</th>`).join('');
  const tbody = rows.length
    ? rows.map((r) => `<tr>${columns.map((c) => `<td>${esc(c.value(r))}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${columns.length}" style="text-align:center">Sin registros</td></tr>`;

  const win = window.open('', '_blank');
  if (!win) {
    alert('Permite las ventanas emergentes para descargar el PDF.');
    return;
  }
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #1a1a1a; }
      h1 { font-size: 18px; margin-bottom: 16px; color: #4a3470; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
      th { background: #4a3470; color: #fff; }
      tr:nth-child(even) td { background: #f4f7fa; }
    </style></head>
    <body><h1>${esc(title)}</h1>
    <table><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody></table>
    <script>window.onload = function () { window.print(); };<\/script>
    </body></html>`);
  win.document.close();
};

// Componente de tabla para Encapsulado
const EncapsulationTable = ({ rows, editingId, onEdit, onSave, onAddNew, onDelete }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA DE<br/>PRODUCCIÓN</th>
          <th>PESO NETO<br/>POR CÁPSULA</th>
          <th>PESO POR<br/>CÁPSULA</th>
          <th>CANTIDAD PRODUCIDA<br/>EN BOTELLAS</th>
          <th>NOMBRE DEL<br/>OPERADOR</th>
          <th>NOTAS</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';
          const operatorName = row.production_process?.operator_name || row.operator_name || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? parseLocalDate(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.net_weight_per_capsule || ''}
                    onChange={(e) => handleFieldChange('net_weight_per_capsule', e.target.value)}
                    placeholder="0 MCG"
                  />
                ) : (
                  `${row.net_weight_per_capsule || 0} MCG`
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.actual_weight_per_capsule || ''}
                    onChange={(e) => handleFieldChange('actual_weight_per_capsule', e.target.value)}
                    placeholder="0 MCG"
                  />
                ) : (
                  `${row.actual_weight_per_capsule || 0} MCG`
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.bottles_produced || ''}
                    onChange={(e) => handleFieldChange('bottles_produced', e.target.value)}
                    placeholder="250"
                  />
                ) : (
                  row.bottles_produced || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.operator_name || ''}
                    onChange={(e) => handleFieldChange('operator_name', e.target.value)}
                  />
                ) : (
                  operatorName || '-'
                )}
              </td>
              <td className="notes-cell">
                {isEditing ? (
                  <textarea
                    className="inline-textarea"
                    value={currentRow.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    placeholder="Notas..."
                    rows="2"
                  />
                ) : (
                  <span className="notes-text">{(row.production_process?.notes ?? row.notes) || '-'}</span>
                )}
              </td>
              <td className="actions-column">
                <RowActions
                  isEditing={isEditing}
                  isNew={row.id === 'new'}
                  onSave={() => { onSave(currentRow); setEditedRow({}); }}
                  onEdit={() => onEdit(row)}
                  onDelete={() => onDelete(row)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Tés (TEA_PACKING)
const TeaPackingTable = ({ rows, editingId, onEdit, onSave, onAddNew, onDelete }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA DE<br/>EMPAQUETADO</th>
          <th>UNIDADES<br/>EMPAQUETADAS</th>
          <th>NÚMERO DE<br/>LOTE</th>
          <th>NOMBRE DE<br/>COMPAÑÍA</th>
          <th>NOMBRE DEL<br/>EMPACADOR</th>
          <th>NOTAS</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';
          const batchNumber = row.production_process?.batch_number || row.batch_number || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? parseLocalDate(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.packed_units || ''}
                    onChange={(e) => handleFieldChange('packed_units', e.target.value)}
                  />
                ) : (
                  row.packed_units || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.batch_number || ''}
                    onChange={(e) => handleFieldChange('batch_number', e.target.value)}
                    placeholder="0000"
                  />
                ) : (
                  batchNumber || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  />
                ) : (
                  row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.packer_name || ''}
                    onChange={(e) => handleFieldChange('packer_name', e.target.value)}
                  />
                ) : (
                  row.packer_name || '-'
                )}
              </td>
              <td className="notes-cell">
                {isEditing ? (
                  <textarea
                    className="inline-textarea"
                    value={currentRow.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    placeholder="Notas..."
                    rows="2"
                  />
                ) : (
                  <span className="notes-text">{(row.production_process?.notes ?? row.notes) || '-'}</span>
                )}
              </td>
              <td className="actions-column">
                <RowActions
                  isEditing={isEditing}
                  isNew={row.id === 'new'}
                  onSave={() => { onSave(currentRow); setEditedRow({}); }}
                  onEdit={() => onEdit(row)}
                  onDelete={() => onDelete(row)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Empacado (PACKAGING)
const PackagingTable = ({ rows, editingId, onEdit, onSave, onAddNew, onDelete }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA DE<br/>EMPAQUETADO</th>
          <th>UNIDADES<br/>EMPAQUETADAS</th>
          <th>NÚMERO DE<br/>LOTE</th>
          <th>NOMBRE DE<br/>COMPAÑÍA</th>
          <th>NOMBRE DEL<br/>EMPACADOR</th>
          <th>NOTAS</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';
          const batchNumber = row.production_process?.batch_number || row.batch_number || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? parseLocalDate(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.packed_units || ''}
                    onChange={(e) => handleFieldChange('packed_units', e.target.value)}
                  />
                ) : (
                  row.packed_units || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.batch_number || ''}
                    onChange={(e) => handleFieldChange('batch_number', e.target.value)}
                    placeholder="0000"
                  />
                ) : (
                  batchNumber || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  />
                ) : (
                  row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.packer_name || ''}
                    onChange={(e) => handleFieldChange('packer_name', e.target.value)}
                  />
                ) : (
                  row.packer_name || '-'
                )}
              </td>
              <td className="notes-cell">
                {isEditing ? (
                  <textarea
                    className="inline-textarea"
                    value={currentRow.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    placeholder="Notas..."
                    rows="2"
                  />
                ) : (
                  <span className="notes-text">{(row.production_process?.notes ?? row.notes) || '-'}</span>
                )}
              </td>
              <td className="actions-column">
                <RowActions
                  isEditing={isEditing}
                  isNew={row.id === 'new'}
                  onSave={() => { onSave(currentRow); setEditedRow({}); }}
                  onEdit={() => onEdit(row)}
                  onDelete={() => onDelete(row)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Limpieza (CLEANING) - Con checkboxes
const CleaningTable = ({ rows, editingId, onEdit, onSave, onAddNew, onDelete }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table proceso-table--cleaning">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>FECHA</th>
          <th>LIMPIEZA<br/>PROFUNDA</th>
          <th>LIMPIEZA<br/>SEMANAL</th>
          <th colSpan="4" className="areas-header">ÁREAS</th>
          <th>NOMBRE</th>
          <th>NOTAS</th>
          <th className="actions-column"></th>
        </tr>
        <tr className="sub-header">
          <th></th>
          <th></th>
          <th></th>
          <th></th>
          <th className="area-column">PRODUCCIÓN</th>
          <th className="area-column">ALMACÉN</th>
          <th className="area-column">EXTERIOR</th>
          <th className="area-column">BAÑO</th>
          <th></th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const processDate = row.production_process?.process_date || row.process_date || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? parseLocalDate(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.deep_cleaning || false}
                    onChange={(e) => handleCheckboxChange('deep_cleaning', e.target.checked)}
                  />
                ) : (
                  currentRow.deep_cleaning ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.weekly_cleaning || false}
                    onChange={(e) => handleCheckboxChange('weekly_cleaning', e.target.checked)}
                  />
                ) : (
                  currentRow.weekly_cleaning ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.production_area || false}
                    onChange={(e) => handleCheckboxChange('production_area', e.target.checked)}
                  />
                ) : (
                  currentRow.production_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.warehouse_area || false}
                    onChange={(e) => handleCheckboxChange('warehouse_area', e.target.checked)}
                  />
                ) : (
                  currentRow.warehouse_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.outside_area || false}
                    onChange={(e) => handleCheckboxChange('outside_area', e.target.checked)}
                  />
                ) : (
                  currentRow.outside_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.bathroom_area || currentRow.restroom || false}
                    onChange={(e) => handleCheckboxChange('bathroom_area', e.target.checked)}
                  />
                ) : (
                  (currentRow.bathroom_area || currentRow.restroom) ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.cleaner_name || currentRow.operator_name || ''}
                    onChange={(e) => {
                      handleFieldChange('cleaner_name', e.target.value);
                      handleFieldChange('operator_name', e.target.value);
                    }}
                  />
                ) : (
                  row.cleaner_name || row.operator_name || '-'
                )}
              </td>
              <td className="notes-cell">
                {isEditing ? (
                  <textarea
                    className="inline-textarea"
                    value={currentRow.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    placeholder="Notas..."
                    rows="2"
                  />
                ) : (
                  <span className="notes-text">{(row.production_process?.notes ?? row.notes) || '-'}</span>
                )}
              </td>
              <td className="actions-column">
                <RowActions
                  isEditing={isEditing}
                  isNew={row.id === 'new'}
                  onSave={() => { onSave(currentRow); setEditedRow({}); }}
                  onEdit={() => onEdit(row)}
                  onDelete={() => onDelete(row)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Recepción (RECEIVING)
const ReceivingTable = ({ rows, editingId, onEdit, onSave, onAddNew, onDelete }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA</th>
          <th>CAJAS<br/>RECIBIDAS</th>
          <th>PESO POR<br/>CAJA</th>
          <th>TOTAL<br/>RECIBIDO</th>
          <th>COMPAÑÍA</th>
          <th>NOMBRE DEL<br/>EMPACADOR</th>
          <th>NOTAS</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? parseLocalDate(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.received_boxes || ''}
                    onChange={(e) => handleFieldChange('received_boxes', e.target.value)}
                  />
                ) : (
                  row.received_boxes || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.weight_per_box || ''}
                    onChange={(e) => handleFieldChange('weight_per_box', e.target.value)}
                  />
                ) : (
                  row.weight_per_box || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.total_received || ''}
                    onChange={(e) => handleFieldChange('total_received', e.target.value)}
                  />
                ) : (
                  row.total_received || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.supplier_name || currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('supplier_name', e.target.value)}
                  />
                ) : (
                  row.supplier_name || row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.received_by_name || currentRow.receiver_name || ''}
                    onChange={(e) => handleFieldChange('received_by_name', e.target.value)}
                  />
                ) : (
                  row.received_by_name || row.receiver_name || '-'
                )}
              </td>
              <td className="notes-cell">
                {isEditing ? (
                  <textarea
                    className="inline-textarea"
                    value={currentRow.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    placeholder="Notas..."
                    rows="2"
                  />
                ) : (
                  <span className="notes-text">{(row.production_process?.notes ?? row.notes) || '-'}</span>
                )}
              </td>
              <td className="actions-column">
                <RowActions
                  isEditing={isEditing}
                  isNew={row.id === 'new'}
                  onSave={() => { onSave(currentRow); setEditedRow({}); }}
                  onEdit={() => onEdit(row)}
                  onDelete={() => onDelete(row)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Retornos (RETURNS)
const ReturnsTable = ({ rows, editingId, onEdit, onSave, onAddNew, onDelete }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA</th>
          <th>CANTIDAD<br/>RETORNADA</th>
          <th>NOMBRE DEL<br/>CLIENTE</th>
          <th>ESTADO DEL PRODUCTO<br/>(MALO O BUENO)</th>
          <th>NOTAS</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? parseLocalDate(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.returned_quantity || currentRow.quantity_returned || ''}
                    onChange={(e) => handleFieldChange('returned_quantity', e.target.value)}
                  />
                ) : (
                  row.returned_quantity || row.quantity_returned || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.client_name || ''}
                    onChange={(e) => handleFieldChange('client_name', e.target.value)}
                  />
                ) : (
                  row.client_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <select
                    className="inline-input"
                    value={currentRow.return_status || 'GOOD'}
                    onChange={(e) => handleFieldChange('return_status', e.target.value)}
                  >
                    <option value="GOOD">BUENO</option>
                    <option value="BAD">MALO</option>
                  </select>
                ) : (
                  row.return_status === 'GOOD' ? 'BUENO' : row.return_status === 'BAD' ? 'MALO' : row.return_status || 'BUENO'
                )}
              </td>
              <td className="notes-cell">
                {isEditing ? (
                  <textarea
                    className="inline-textarea"
                    value={currentRow.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                    placeholder="Notas..."
                    rows="2"
                  />
                ) : (
                  <span className="notes-text">{(row.production_process?.notes ?? row.notes) || '-'}</span>
                )}
              </td>
              <td className="actions-column">
                <RowActions
                  isEditing={isEditing}
                  isNew={row.id === 'new'}
                  onSave={() => { onSave(currentRow); setEditedRow({}); }}
                  onEdit={() => onEdit(row)}
                  onDelete={() => onDelete(row)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Mantenimiento (MAINTENANCE)
const MaintenanceTable = ({ rows, editingId, onEdit, onSave, onAddNew, onDelete }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>EQUIPO</th>
          <th>FECHA</th>
          <th>NOMBRE DEL<br/>TÉCNICO</th>
          <th>NOTA</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const processDate = row.production_process?.process_date || row.process_date || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.equipment_name || ''}
                    onChange={(e) => handleFieldChange('equipment_name', e.target.value)}
                  />
                ) : (
                  row.equipment_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? parseLocalDate(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.technician_name || ''}
                    onChange={(e) => handleFieldChange('technician_name', e.target.value)}
                  />
                ) : (
                  row.technician_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.description || currentRow.notes || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Agregar nota..."
                  />
                ) : (
                  row.description || row.notes || '-'
                )}
              </td>
              <td className="actions-column">
                <RowActions
                  isEditing={isEditing}
                  isNew={row.id === 'new'}
                  onSave={() => { onSave(currentRow); setEditedRow({}); }}
                  onEdit={() => onEdit(row)}
                  onDelete={() => onDelete(row)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ProcesosList;
